import React, { Fragment, useCallback, useState } from "react";

import { formatNumber, useSoilSightCurrency, useTranslation } from "utils";

import { ROOT_FONT_SIZE_PX } from "consts";

import { Icon, Tooltip, Typography } from "components";

import { HorizontalScrollFade } from "pages/app/scenario-analysis/components/HorizontalScrollFade";
import { OutcomeTooltip } from "pages/app/scenario-analysis/components/OutcomeTooltip";
import { ExpandCell, FactorRows } from "pages/app/scenario-analysis/components/cm-comparison-table/FactorRows";
import { SnapshotHeader } from "pages/app/scenario-analysis/components/cm-comparison-table/SnapshotHeader";
import { useCMComparisonModel } from "pages/app/scenario-analysis/hooks/useCMComparisonModel";
import { useCmBreakdownFlag } from "pages/app/scenario-analysis/hooks/useCmBreakdownFlag";
import { useMeasuredWidth } from "pages/app/scenario-analysis/hooks/useMeasuredWidth";
import { useYearLabel } from "pages/app/scenario-analysis/hooks/useYearLabel";
import { ScenarioData } from "pages/app/scenario-analysis/types/scenario";
import { inlinePctDisplay } from "pages/app/scenario-analysis/utils/comparisonFormatting";
import {
  ForecastDatum,
  SoilSightOutcomesByScenario,
  ViewMode,
  forecastKey,
  getBandKinds,
  getStretchedYearColumnWidthPx,
  getYearData,
  scenarioLineColorAt,
  shouldUseOutcomeYearScroll,
} from "pages/app/scenario-analysis/utils/forecast";
import { reportsImpactFactor } from "pages/app/scenario-analysis/utils/impactFactors";
import { calcUpliftPercent } from "pages/app/scenario-analysis/utils/uplift";

type CMComparisonTableProps = {
  scenarios: ScenarioData[];
  projectionYears: number;
  forecastData?: ForecastDatum[];
  viewMode?: ViewMode;
  mainCropLabelLookup?: Record<string, string>;
  soilSightOutcomes?: SoilSightOutcomesByScenario | null;
  marketPricePct?: number;
  costDevelopmentPct?: number;
  forceExpandAll?: boolean;
  dataTestId?: string;
};

export const CMComparisonTable = ({
  scenarios,
  projectionYears,
  forecastData: forecastProp,
  viewMode = "cumulative",
  mainCropLabelLookup = {},
  soilSightOutcomes = null,
  marketPricePct = 0,
  costDevelopmentPct = 0,
  forceExpandAll = false,
  dataTestId = "cm-comparison-table",
}: CMComparisonTableProps) => {
  const { t } = useTranslation("scenarioAnalysis");
  const isCmBreakdownEnabled = useCmBreakdownFlag();
  const { formatCurrency, formatCmOutcome } = useSoilSightCurrency();

  const [expandedScenarios, setExpandedScenarios] = useState<Set<string>>(() => new Set());
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(() => new Set());

  const {
    forecast,
    allYears,
    displayYears,
    isAllYearsLayout,
    showTotalCol,
    baselineIdx,
    leafByScenarioYear,
    snapshotCellByScenario,
    getParentValue,
    getAnnualTotal,
    getBaselineTotal,
  } = useCMComparisonModel({ scenarios, projectionYears, forecastData: forecastProp, viewMode, soilSightOutcomes });
  const { formatYear } = useYearLabel();

  const isYearScrollEnabled = isAllYearsLayout && shouldUseOutcomeYearScroll(projectionYears);
  // Only the scrolling year layout reads the container width, so the other layouts don't re-render on resize.
  const [tableContainerRef, tableContainerWidth] = useMeasuredWidth<HTMLDivElement>({ enabled: isYearScrollEnabled });
  // Sticky right group: Total column (5rem, annual only) + expand column (2rem). The scroll chrome sits just left of it.
  const stickyRightWidthRem = (showTotalCol ? 5 : 0) + 2;
  // Sticky scenario column (11rem), scroll-fade chrome (3rem) and the sticky right group sit outside the year band.
  const yearBandOverheadRem = 11 + 3 + stickyRightWidthRem;
  // Year columns stretch so OUTCOME_VISIBLE_YEAR_CAP of them fill the card, like the chart's bar groups.
  const yearColumnWidthPx = getStretchedYearColumnWidthPx(tableContainerWidth - yearBandOverheadRem * ROOT_FONT_SIZE_PX);
  // Published in rem, never px: printing shrinks the root font so the layout fits A4, which a px width would ignore.
  const yearColumnWidthRem = yearColumnWidthPx / ROOT_FONT_SIZE_PX;
  const tableModeClass = isAllYearsLayout ? "outcome-comparison-table--annual-yield" : "outcome-comparison-table--cumulative";

  const toggleScenario = useCallback(
    (id: string) => {
      if (forceExpandAll) return;
      setExpandedScenarios((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    },
    [forceExpandAll]
  );

  const toggleCategory = useCallback(
    (key: string) => {
      if (forceExpandAll) return;
      setExpandedCategories((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
    },
    [forceExpandAll]
  );

  const fmtCell = useCallback(
    (value: number) =>
      viewMode === "yield" ? formatNumber({ number: value, decimalPlaces: 1 }) : formatCurrency(value, { decimals: 0, signDisplay: "always" }),
    [viewMode, formatCurrency]
  );

  const formatTooltipValue = useCallback(
    (value: number): string => (viewMode === "yield" ? formatNumber({ number: value, decimalPlaces: 1 }) : formatCmOutcome(value)),
    [viewMode, formatCmOutcome]
  );

  const buildCellTooltip = useCallback(
    ({
      scenarioIdx,
      year,
      value,
      pctDelta,
      headerOverride,
    }: {
      scenarioIdx: number;
      year: number;
      value: number;
      pctDelta: number | null;
      headerOverride?: string;
    }): React.ReactNode => {
      const scenario = scenarios[scenarioIdx];
      const row = getYearData(forecast, year);
      const cropId = row ? String(row[forecastKey(scenarioIdx, "cropId")] ?? "") : "";
      // The Total column passes its own header and has no band.
      const bandKinds = headerOverride === undefined ? getBandKinds(viewMode) : null;
      const low = bandKinds ? Number(row?.[forecastKey(scenarioIdx, bandKinds.low)] ?? value) : value;
      const high = bandKinds ? Number(row?.[forecastKey(scenarioIdx, bandKinds.high)] ?? value) : value;
      const showRange = bandKinds !== null && formatTooltipValue(low) !== formatTooltipValue(high);
      const pctDisplay = pctDelta !== null ? inlinePctDisplay(pctDelta) : null;

      return (
        <OutcomeTooltip
          header={headerOverride ?? formatYear(year)}
          rows={[
            {
              key: scenario.id,
              color: scenarioLineColorAt(scenarioIdx),
              name: (cropId && mainCropLabelLookup[cropId]) || scenario.title,
              value: formatTooltipValue(value),
              range: showRange ? `${formatTooltipValue(low)} – ${formatTooltipValue(high)}` : undefined,
              rangeTestId: "outcome-comparison-tooltip-range",
              footer: pctDisplay ? (
                <div className={`outcome-comparison-tooltip-delta ${pctDisplay.className}`}>
                  {pctDisplay.label} {t("outcomeComparison.tooltipVsBaseline")}
                </div>
              ) : null,
            },
          ]}
        />
      );
    },
    [scenarios, forecast, mainCropLabelLookup, viewMode, formatTooltipValue, formatYear, t]
  );

  const tableContent = (
    <table className={`outcome-comparison-table ${tableModeClass}`} data-testid={`${dataTestId}-table`}>
      <SnapshotHeader displayYears={displayYears} isAllYearsLayout={isAllYearsLayout} viewMode={viewMode} showTotalCol={showTotalCol} />
      <tbody>
        {scenarios.map((scenario, idx) => {
          const lineColor = scenarioLineColorAt(idx);
          const rowTestId = `${dataTestId}-row-${scenario.id}`;
          const apiYears = soilSightOutcomes?.[scenario.id];
          const canExpand = Boolean(apiYears?.length);
          const isExpanded = forceExpandAll || expandedScenarios.has(scenario.id);
          const scenarioSnapshotByYear = snapshotCellByScenario[scenario.id] ?? {};

          const leafTables = leafByScenarioYear[scenario.id];

          return (
            <Fragment key={scenario.id}>
              <tr
                className={`outcome-comparison-table-row outcome-comparison-table-row--parent ${
                  idx > 0 ? "outcome-comparison-table-row--scenario-divider" : ""
                } ${canExpand ? "outcome-comparison-table-row--expandable" : ""} ${isExpanded ? "is-expanded" : ""} ${
                  idx === baselineIdx ? "is-baseline" : ""
                }`}
                data-testid={rowTestId}
                {...(canExpand
                  ? {
                      onClick: () => toggleScenario(scenario.id),
                    }
                  : {})}
              >
                <th scope="row" className="outcome-comparison-table-name outcome-comparison-table-name-sticky">
                  <span className="outcome-comparison-table-name-dot" style={{ backgroundColor: lineColor }} aria-hidden />
                  <Typography variant="paragraph-m" className="outcome-comparison-table-title">
                    {scenario.title}
                  </Typography>
                </th>
                {displayYears.map((year) => {
                  const value = getParentValue(idx, year);
                  const baselineValue = getBaselineTotal(year);
                  const pctChange = !scenario.isBaseline ? calcUpliftPercent(baselineValue, value) : null;
                  const cellCm = viewMode === "cumulative" ? scenarioSnapshotByYear[year]?.cm ?? value : value;

                  const displayValue = viewMode === "yield" ? fmtCell(value) : formatCmOutcome(cellCm);

                  return (
                    <td key={year} className="outcome-comparison-table-cell" onClick={(e) => canExpand && e.stopPropagation()}>
                      <div className="outcome-comparison-table-cell-inner">
                        <Tooltip
                          title={buildCellTooltip({ scenarioIdx: idx, year, value, pctDelta: pctChange })}
                          placement="top"
                          variant="neutral"
                          style={{ width: "100%", textAlign: "right" }}
                        >
                          <span className="outcome-comparison-table-cm-stack">
                            {!isAllYearsLayout && pctChange !== null
                              ? (() => {
                                  const pctDisplay = inlinePctDisplay(pctChange);
                                  return (
                                    <span className={`outcome-comparison-table-change ${pctDisplay.className}`} data-testid={`${rowTestId}-y${year}-change`}>
                                      <Icon iconName={pctDisplay.icon} size="xs" />
                                      {pctDisplay.label}
                                    </span>
                                  );
                                })()
                              : null}
                            <span className="outcome-comparison-table-cm" data-testid={`${rowTestId}-y${year}-cm`}>
                              {displayValue}
                            </span>
                          </span>
                        </Tooltip>
                      </div>
                    </td>
                  );
                })}
                {showTotalCol ? (
                  <td className="outcome-comparison-table-cell outcome-comparison-table-cell--total" onClick={(e) => canExpand && e.stopPropagation()}>
                    <div className="outcome-comparison-table-cell-inner">
                      {(() => {
                        const totalVal = getAnnualTotal(idx);
                        const baselineTotalVal = baselineIdx >= 0 ? getAnnualTotal(baselineIdx) : 0;
                        const totalPctChange = !scenario.isBaseline ? calcUpliftPercent(baselineTotalVal, totalVal) : null;
                        const totalDisplay = formatCmOutcome(totalVal);
                        return (
                          <Tooltip
                            title={buildCellTooltip({
                              scenarioIdx: idx,
                              year: projectionYears,
                              value: totalVal,
                              pctDelta: totalPctChange,
                              headerOverride: t("outcomeComparison.column.total"),
                            })}
                            placement="top"
                            variant="neutral"
                            style={{ width: "100%", textAlign: "right" }}
                          >
                            <span className="outcome-comparison-table-cm" data-testid={`${rowTestId}-total-cm`}>
                              {totalDisplay}
                            </span>
                          </Tooltip>
                        );
                      })()}
                    </div>
                  </td>
                ) : null}
                <ExpandCell chevron={canExpand ? { expanded: isExpanded } : undefined} />
              </tr>

              {isExpanded && canExpand ? (
                <FactorRows
                  scenarioId={scenario.id}
                  lineColor={lineColor}
                  displayYears={displayYears}
                  allYears={allYears}
                  leafTables={leafTables}
                  isFactorReported={(factorKey) => reportsImpactFactor(apiYears, factorKey)}
                  isCmBreakdownEnabled={isCmBreakdownEnabled}
                  viewMode={viewMode}
                  showTotalCol={showTotalCol}
                  marketPricePct={marketPricePct}
                  costDevelopmentPct={costDevelopmentPct}
                  forceExpandAll={forceExpandAll}
                  expandedCategories={expandedCategories}
                  onToggleCategory={toggleCategory}
                  formatCell={fmtCell}
                  dataTestId={dataTestId}
                />
              ) : null}
            </Fragment>
          );
        })}
      </tbody>
    </table>
  );

  const wrappedTable = <div className="outcome-comparison-table-wrapper outcome-comparison-table-wrapper--impact">{tableContent}</div>;

  return (
    <div
      ref={tableContainerRef}
      className={`outcome-comparison-impact${forceExpandAll ? " outcome-comparison-impact--static" : ""}`}
      style={isYearScrollEnabled ? ({ "--outcome-year-column-width": `${yearColumnWidthRem}rem` } as React.CSSProperties) : undefined}
      data-testid={dataTestId}
    >
      {isYearScrollEnabled ? (
        <HorizontalScrollFade dataTestId={`${dataTestId}-scroll`} enabled chromeOffsetRight={stickyRightWidthRem} chromeOffsetLeft={11}>
          {wrappedTable}
        </HorizontalScrollFade>
      ) : (
        wrappedTable
      )}
    </div>
  );
};

export default CMComparisonTable;
